// client/src/components/chat/MessageList.js
import { useEffect, useRef, useState, memo } from 'react'
import { format, isToday, isYesterday } from 'date-fns'
import useAuth from '../../hooks/useAuth'
import useChannelName from '../../hooks/useChannelName'
import { shouldAutoScroll } from '../../utils/chatUtils'

function MessageList({
  messages,
  channel,
  loading,
  onEditMessage,
  onDeleteMessage,
  onLoadMore,
  getTypingUsersForChannel,
  getOnlineUsersForChannel,
}) {
  const { user } = useAuth()
  const messagesEndRef = useRef(null)
  const messageListRef = useRef(null)
  const [shouldAutoScrollState, setShouldAutoScrollState] = useState(true)
  const [editingMessageId, setEditingMessageId] = useState(null)
  const [editingText, setEditingText] = useState('')
  const [confirmDeleteId, setConfirmDeleteId] = useState(null)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const channelName = useChannelName(channel)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (!shouldAutoScrollState) return

    // Prefer scrolling the message list container so the page doesn't jump
    if (messageListRef.current) {
      const el = messageListRef.current
      el.scrollTop = el.scrollHeight
      return
    }

    // Fallback (should rarely run): keep for safety but avoid smooth to reduce page jump
    if (messagesEndRef.current) {
      try {
        messagesEndRef.current.scrollIntoView({
          behavior: 'auto',
          block: 'end',
        })
      } catch {
        // Ignore scrollIntoView errors
      }
    }
  }, [messages, shouldAutoScrollState])

  // Handle scroll events to disable auto-scroll when user scrolls up
  // Also handle infinite scroll for loading more messages
  const handleScroll = (e) => {
    const element = e.target
    const isAtBottom = shouldAutoScroll(element)
    setShouldAutoScrollState(isAtBottom)

    // Check if user scrolled near the top for infinite scroll
    if (onLoadMore && !isLoadingMore && !loading) {
      const scrollTop = element.scrollTop
      const threshold = 100 // pixels from top

      if (scrollTop <= threshold && messages.length > 0) {
        handleLoadMore()
      }
    }
  }

  // Load more messages when user scrolls to top
  const handleLoadMore = async () => {
    if (!channel || isLoadingMore || loading) return

    setIsLoadingMore(true)
    try {
      const hasMore = await onLoadMore(channel)

      // If we loaded more messages, maintain scroll position
      if (hasMore && messageListRef.current) {
        // Get current scroll position
        const scrollElement = messageListRef.current
        const oldScrollHeight = scrollElement.scrollHeight
        const oldScrollTop = scrollElement.scrollTop

        // After messages are loaded, adjust scroll position to maintain view
        // We'll use setTimeout to ensure DOM has updated
        setTimeout(() => {
          if (scrollElement) {
            const newScrollHeight = scrollElement.scrollHeight
            const scrollDifference = newScrollHeight - oldScrollHeight
            scrollElement.scrollTop = oldScrollTop + scrollDifference
          }
        }, 0)
      }
    } catch (error) {
      console.error('Error loading more messages:', error)
    } finally {
      setIsLoadingMore(false)
    }
  }

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return 'Unknown time'

    const date = new Date(timestamp)
    if (isNaN(date.getTime())) {
      console.warn('Invalid timestamp:', timestamp)
      return 'Invalid time'
    }

    if (isToday(date)) {
      return format(date, 'h:mm a')
    } else if (isYesterday(date)) {
      return `Yesterday ${format(date, 'h:mm a')}`
    } else {
      return format(date, 'MMM d, h:mm a')
    }
  }

  const canEditMessage = (message) => {
    if (!user || message.sender_player_id !== user.primary_player_id)
      return false
    if (message.deleted_at) return false

    // Check if within 1 minute
    const now = new Date()
    const createdAt = new Date(message.created_at)
    const timeDiff = now - createdAt
    return timeDiff < 60 * 1000 // 1 minute in milliseconds
  }

  const canDeleteMessage = (message) => {
    return (
      user &&
      message.sender_player_id === user.primary_player_id &&
      !message.deleted_at
    )
  }

  const handleEditMessage = (message) => {
    setEditingMessageId(message.id)
    setEditingText(message.body)
  }

  const handleSaveEdit = async () => {
    if (!editingText.trim() || !onEditMessage) return

    try {
      await onEditMessage(editingMessageId, editingText.trim())
      setEditingMessageId(null)
      setEditingText('')
    } catch (error) {
      console.error('Failed to edit message:', error)
      window.alert('Failed to edit message: ' + error.message)
    }
  }

  const handleCancelEdit = () => {
    setEditingMessageId(null)
    setEditingText('')
  }

  const handleRequestDelete = (messageId) => {
    setConfirmDeleteId(messageId)
  }

  const handleConfirmDelete = async (messageId) => {
    if (!onDeleteMessage) return
    try {
      await onDeleteMessage(messageId)
    } catch (error) {
      console.error('Failed to delete message:', error)
      window.alert('Failed to delete message: ' + error.message)
    } finally {
      setConfirmDeleteId(null)
    }
  }

  const handleCancelDelete = () => {
    setConfirmDeleteId(null)
  }

  const formatTypingIndicator = () => {
    if (!getTypingUsersForChannel || !channel) return null

    const typingUsers = getTypingUsersForChannel(channel.id)
    if (!typingUsers || typingUsers.length === 0) return null

    const names = typingUsers.map((user) => user.playerName)
    if (names.length === 1) {
      return `${names[0]} is typing...`
    } else if (names.length === 2) {
      return `${names[0]} and ${names[1]} are typing...`
    } else {
      return `${names.slice(0, -1).join(', ')} and ${
        names[names.length - 1]
      } are typing...`
    }
  }

  const getDeliveryStatusIcon = (status) => {
    switch (status) {
      case 'sending':
        return '⏳'
      case 'sent':
        return '✓'
      case 'delivered':
        return '✓✓'
      case 'failed':
        return '❌'
      default:
        return '✓✓' // Default to delivered for older messages
    }
  }

  const getDeliveryStatusTitle = (status) => {
    switch (status) {
      case 'sending':
        return 'Sending...'
      case 'sent':
        return 'Sent'
      case 'delivered':
        return 'Delivered'
      case 'failed':
        return 'Failed to send'
      default:
        return 'Delivered'
    }
  }

  if (loading) {
    return (
      <>
        <div className="chat-header">
          <h3 className="chat-header-title">{channelName}</h3>
        </div>
        <div className="empty-state" role="status" aria-live="polite">
          <div className="loading-spinner" aria-hidden="true"></div>
          <p>Loading messages...</p>
        </div>
      </>
    )
  }

  if (!channel) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">💬</div>
        <h4 className="empty-state-title">Select a Channel</h4>
        <p className="empty-state-text">
          Choose a channel from the sidebar to start chatting
        </p>
      </div>
    )
  }

  return (
    <>
      <div className="chat-header">
        <h3 className="chat-header-title" id="chat-channel-name">
          {channelName}
        </h3>
        {getOnlineUsersForChannel && channel && (
          <div className="online-users-indicator">
            {(() => {
              const onlineUsers = getOnlineUsersForChannel(channel.id)
              const onlineCount = onlineUsers.filter(
                (user) => user.isOnline,
              ).length

              if (onlineCount === 0) return null

              return (
                <div
                  className="online-status"
                  role="status"
                  aria-label={`${onlineCount} ${onlineCount === 1 ? 'user' : 'users'} online: ${onlineUsers.map((u) => u.playerName).join(', ')}`}
                >
                  <span className="online-dot" aria-hidden="true"></span>
                  <span className="online-text" aria-hidden="true">
                    {onlineCount === 1 ? '1 online' : `${onlineCount} online`}
                  </span>
                </div>
              )
            })()}
          </div>
        )}
      </div>

      <div
        className="message-list"
        onScroll={handleScroll}
        ref={messageListRef}
        role="log"
        aria-live="polite"
        aria-atomic="false"
        aria-relevant="additions"
        aria-labelledby="chat-channel-name"
      >
        {/* Loading indicator for infinite scroll */}
        {isLoadingMore && (
          <div className="load-more-indicator" role="status" aria-live="polite">
            <div className="loading-spinner" aria-hidden="true"></div>
            <p>Loading older messages...</p>
          </div>
        )}

        {messages.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">💬</div>
            <h4 className="empty-state-title">No Messages Yet</h4>
            <p className="empty-state-text">
              Be the first to send a message in this channel!
            </p>
          </div>
        ) : (
          <>
            {messages.map((message) => {
              const isOwn =
                user &&
                (message.sender_player_id === user.primary_player_id ||
                  message.isPending === true)
              const isEditing = editingMessageId === message.id
              const senderName =
                `${message.firstname} ${message.lastname || ''}`.trim()
              const messageTime = formatMessageTime(message.created_at)

              return (
                <div
                  key={message.id}
                  className={`message ${isOwn ? 'own' : 'other'}`}
                  role="article"
                  aria-label={`Message from ${isOwn ? 'you' : senderName} at ${messageTime}`}
                >
                  {!isOwn && (
                    <div className="message-sender-name">
                      {senderName}
                    </div>
                  )}

                  <div className="message-content-wrapper">
                    <div className="message-bubble">
                      {isEditing ? (
                        <div className="message-edit-form">
                          <label
                            htmlFor={`edit-message-${message.id}`}
                            className="sr-only"
                          >
                            Edit message
                          </label>
                          <textarea
                            id={`edit-message-${message.id}`}
                            value={editingText}
                            onChange={(e) => setEditingText(e.target.value)}
                            className="message-edit-input"
                            rows="2"
                            maxLength="1000"

                          />
                          <div className="message-edit-actions">
                            <button
                              onClick={handleSaveEdit}
                              className="btn btn-sm btn-primary"
                              disabled={!editingText.trim()}
                              aria-label="Save edited message"
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="btn btn-sm btn-secondary"
                              aria-label="Cancel editing"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="message-text">{message.body}</div>
                          <div className="message-meta">
                            <span className="message-time">
                              {formatMessageTime(message.created_at)}
                            </span>
                            {message.edited_at && (
                              <span
                                className="message-edited-label"
                                title="Edited"
                                aria-label="Message was edited"
                              >
                                (edited)
                              </span>
                            )}
                            {isOwn && (
                              <span
                                className="delivery-status"
                                title={getDeliveryStatusTitle(
                                  message.delivery_status,
                                )}

                                aria-hidden="true"
                              >
                                {getDeliveryStatusIcon(message.delivery_status)}
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    {!isEditing && (
                      <div className="message-actions">
                        <button
                          onClick={() => handleEditMessage(message)}
                          className="message-action-btn"
                          aria-label={
                            canEditMessage(message)
                              ? 'Edit message'
                              : 'You can only edit your own messages within 1 minute'
                          }
                          disabled={!canEditMessage(message)}
                        >
                          ✏️
                        </button>
                        {confirmDeleteId === message.id ? (
                          <span className="delete-confirm-wrapper">
                            <button
                              onClick={() => handleConfirmDelete(message.id)}
                              className="btn btn-xs btn-danger"
                              aria-label="Confirm delete message"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={handleCancelDelete}
                              className="btn btn-xs btn-outline-secondary"
                              aria-label="Cancel delete"
                            >
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <button
                            onClick={() => handleRequestDelete(message.id)}
                            className="message-action-btn"
                            aria-label={
                              canDeleteMessage(message)
                                ? 'Delete message'
                                : 'You can only delete your own messages'
                            }
                            disabled={!canDeleteMessage(message)}
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}

            {/* Typing Indicators */}
            {formatTypingIndicator() && (
              <div
                className="typing-indicator"
                role="status"
                aria-live="polite"
                aria-label={formatTypingIndicator()}
              >
                <div className="typing-dots" aria-hidden="true">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span className="typing-text" aria-hidden="true">
                  {formatTypingIndicator()}
                </span>
              </div>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </div>
    </>
  )
}

// Memoize component to prevent unnecessary re-renders when props haven't changed
export default memo(MessageList, (prevProps, nextProps) => {
  // Custom comparison function for better performance
  return (
    prevProps.messages === nextProps.messages &&
    prevProps.channel?.id === nextProps.channel?.id &&
    prevProps.loading === nextProps.loading &&
    prevProps.onEditMessage === nextProps.onEditMessage &&
    prevProps.onDeleteMessage === nextProps.onDeleteMessage &&
    prevProps.onLoadMore === nextProps.onLoadMore &&
    prevProps.getTypingUsersForChannel === nextProps.getTypingUsersForChannel &&
    prevProps.getOnlineUsersForChannel === nextProps.getOnlineUsersForChannel
  )
})
